import { useAchievement } from '../contexts/AchievementContext';
import useAchievementConfig from './useAchievementConfig';

// Achievement types that match the database
export const ACHIEVEMENT_TYPES = {
  WELCOME_WHISKERS: 'welcome_whiskers',
  TASK_MASTER: 'task_master',
  SPINNING_SENSATION: 'spinning_sensation',
  LEARNING_LYNX: 'learning_lynx',
  APPLICATION_ACE: 'application_ace',
  EMAIL_EXPLORER: 'email_explorer',
  STREAK_STAR: 'streak_star',
};

const useAchievementTriggers = () => {
  const {
    showAchievement,
    achievements,
    loading: achievementsLoading,
    error: achievementsError,
    fetchAchievements,
    initializeAchievements,
  } = useAchievement();
  const { achievementConfigs, loading: configLoading, error: configError } = useAchievementConfig();

  // Helper function to check if achievement is already acquired
  const isAchievementAcquired = (achievementId) => {
    console.log('Checking if achievement is acquired:', achievementId);
    console.log('Achievements loading:', achievementsLoading);
    console.log('Achievements error:', achievementsError);
    console.log('Achievements:', achievements);

    if (achievementsLoading) {
      console.log("Achievements still loading, can't verify");
      return false; // Changed from true to false to allow showing achievement while loading
    }

    if (achievementsError) {
      console.log('Error loading achievements:', achievementsError);
      return false; // Changed from true to false to allow showing achievement despite errors
    }

    const isAcquired = achievements.some(
      (achievement) => achievement.id === achievementId && !achievement.locked
    );
    console.log('Is achievement already acquired:', isAcquired);
    return isAcquired;
  };

  // Helper function to show achievement
  const showAndRefreshAchievement = (config) => {
    console.log('Showing achievement with config:', config);

    // Make sure we have all required fields
    if (!config || !config.id || !config.name || !config.description || !config.imageUrl) {
      console.error('Invalid achievement config:', config);
      return;
    }

    // Show the achievement
    showAchievement({
      id: config.id,
      name: config.name,
      description: config.description,
      imageUrl: config.imageUrl,
      alt: config.name,
    });

    // Refresh achievements after a short delay to ensure the achievement is marked as complete
    setTimeout(() => {
      console.log('Refreshing achievements after showing');
      fetchAchievements();
    }, 1000);
  };

  // Generic function to check and trigger an achievement
  const checkAchievement = (achievementType, extraLogging = false) => {
    if (configLoading || configError) return false;

    const config = achievementConfigs[achievementType];
    if (!config || isAchievementAcquired(config.id)) return false;

    if (extraLogging) {
      console.log(`Checking achievement: ${achievementType}`);
    }

    showAndRefreshAchievement(config);
    return true;
  };

  // Function to check and trigger achievements based on conditions
  const checkAchievements = {
    // Welcome achievement - triggered on command after onboarding
    checkFirstVisit: () => checkAchievement(ACHIEVEMENT_TYPES.WELCOME_WHISKERS),

    // Task Master - complete all daily tasks
    checkDailyTasks: () => checkAchievement(ACHIEVEMENT_TYPES.TASK_MASTER),

    // Spinning Sensation - spin the cat
    checkSpinningCat: () => checkAchievement(ACHIEVEMENT_TYPES.SPINNING_SENSATION),

    // Learning Lynx - complete 5 lessons
    checkLessonsCompleted: () => checkAchievement(ACHIEVEMENT_TYPES.LEARNING_LYNX),

    // Application Ace - add 3 applications
    checkApplicationCount: () => checkAchievement(ACHIEVEMENT_TYPES.APPLICATION_ACE),

    // Email Explorer - connect email
    checkEmailConnection: () => checkAchievement(ACHIEVEMENT_TYPES.EMAIL_EXPLORER),

    // Streak Star - 2 day streak DONE
    checkStreak: (streakDays) => checkAchievement(ACHIEVEMENT_TYPES.STREAK_STAR),

    // Refresh achievements - manually refresh the achievements list
    refreshAchievements: () => {
      console.log('Manually refreshing achievements');
      fetchAchievements();
      return true;
    },

    // Initialize achievements - manually initialize the achievements
    initializeAchievements: () => {
      console.log('Manually initializing achievements');
      initializeAchievements();
      return true;
    },
  };

  return checkAchievements;
};

export default useAchievementTriggers;
